// @ts-nocheck
import { getFastlyUrl } from '@corratech/pylot-image'

// Matches the start of a JSON-valued HTML attribute, e.g. `data-additional-config="{`.
const JSON_ATTRIBUTE_START_REGEX = /\s[\w-]+="[[{]/g

/**
 * Some Page Builder / CMS sources (notably a client-side content refetch) serialize
 * JSON-valued attributes (data-background-images, data-locations, data-additional-config, ...)
 * with unescaped quotes inside the value instead of the HTML entity (&quot;) — either
 * backslash-escaped (\") or, less commonly, already entity-decoded (a literal `"`). Neither
 * has any special meaning inside an HTML attribute value, so the browser/JSDOM HTML parser
 * treats the first unescaped quote as the end of the attribute, silently truncating the JSON.
 * This breaks every content type that reads it (e.g. Row/Column background images end up null,
 * or Slider's `additionalConfig` never parses and the carousel falls back to a single slide),
 * even though the exact same content parses fine when it comes from SSR with correct escaping.
 *
 * This walks every `attr="{...}"` / `attr="[...]"` value (found via balanced bracket counting,
 * so nested objects/arrays are handled correctly) and re-escapes any literal quote found inside
 * it to `&quot;` BEFORE the string is parsed as DOM, so `getAttribute()` returns the full, valid
 * JSON regardless of which source produced it. Attributes that are already correctly encoded
 * contain no literal quotes inside the value, so this is a no-op for them.
 *
 * @param {string} html
 * @returns {string}
 */
export function normalizeEscapedJsonAttributes(html) {
    if (!html || (!html.includes('="{') && !html.includes('="['))) {
        return html
    }

    let result = ''
    let cursor = 0
    JSON_ATTRIBUTE_START_REGEX.lastIndex = 0
    let match

    while ((match = JSON_ATTRIBUTE_START_REGEX.exec(html))) {
        const openBracket = match[0].slice(-1)
        const closeBracket = openBracket === '{' ? '}' : ']'
        const valueStart = match.index + match[0].length - 1

        let depth = 0
        let end = -1
        for (let i = valueStart; i < html.length; i++) {
            const ch = html[i]
            if (ch === openBracket) depth++
            else if (ch === closeBracket) {
                depth--
                if (depth === 0) {
                    end = i
                    break
                }
            }
        }

        // No balanced close, or the char right after it isn't the attribute's closing
        // quote — doesn't confidently look like a JSON attribute, leave it untouched.
        if (end === -1 || html[end + 1] !== '"') {
            continue
        }

        const rawValue = html.slice(valueStart, end + 1)
        if (!rawValue.includes('"') && !rawValue.includes('\\"')) {
            continue
        }

        const fixedValue = rawValue
            .replace(/\\"/g, '&quot;')
            .replace(/"/g, '&quot;')

        result += html.slice(cursor, valueStart) + fixedValue
        cursor = end + 1
        JSON_ATTRIBUTE_START_REGEX.lastIndex = end + 1
    }

    if (cursor === 0) {
        return html
    }

    return result + html.slice(cursor)
}

/**
 * Retrieve background images from a master format node
 *
 * @param node
 * @returns {{mobileImage: null, desktopImage: null}}
 */
export function getBackgroundImages(node) {
    if (!node) {
        return {
            desktopImage: null,
            mobileImage: null,
            backgroundSize: null,
            backgroundPosition: null,
            backgroundAttachment: null,
            backgroundRepeat: false
        }
    }
    const images = node.getAttribute('data-background-images')
    const response = {
        desktopImage: null,
        mobileImage: null,
        backgroundSize: readInlineStyleValue(node.style.backgroundSize),
        backgroundPosition: readInlineStyleValue(node.style.backgroundPosition),
        backgroundAttachment: readInlineStyleValue(
            node.style.backgroundAttachment
        ),
        backgroundRepeat: node.style.backgroundRepeat !== 'no-repeat'
    }

    if (images) {
        try {
            // Remove potential surrounding quotes if the attribute was double-quoted
            let sanitizedImages = images.trim()

            // If it's just a placeholder or clearly too short, skip
            if (sanitizedImages.length < 2) {
                return response
            }

            // Handle double-escaped JSON: \"{\"...}\"
            // First, replace all escaped quotes with literal quotes
            sanitizedImages = sanitizedImages.replace(/\\"/g, '"')

            // Remove surrounding quotes if they exist after replacement
            sanitizedImages = sanitizedImages.trim()
            if (
                sanitizedImages.startsWith('"') &&
                sanitizedImages.endsWith('"')
            ) {
                sanitizedImages = sanitizedImages.slice(1, -1)
            }

            // If it's still not valid JSON, it might be using single quotes (rare but possible in custom blocks)
            if (
                sanitizedImages.includes("'") &&
                !sanitizedImages.includes('"')
            ) {
                sanitizedImages = sanitizedImages.replace(/'/g, '"')
            }

            // Final check: must look like a JSON object
            const finalJson = sanitizedImages.trim()
            if (!finalJson.startsWith('{') || !finalJson.endsWith('}')) {
                // If it doesn't start with {, maybe it's still escaped?
                // Let's try one more level of unescaping if it starts with \
                if (finalJson.startsWith('\\')) {
                    const retryJson = finalJson.replace(/\\/g, '')
                    if (retryJson.startsWith('{') && retryJson.endsWith('}')) {
                        const imagesStructure = JSON.parse(retryJson)
                        if (imagesStructure.desktop_image) {
                            response.desktopImage = getFastlyUrl({
                                src: imagesStructure.desktop_image
                            })
                        }
                        if (imagesStructure.mobile_image) {
                            response.mobileImage = getFastlyUrl({
                                src: imagesStructure.mobile_image
                            })
                        }
                        return response
                    }
                }
                return response
            }

            const imagesStructure = JSON.parse(finalJson)
            if (imagesStructure.desktop_image) {
                response.desktopImage = getFastlyUrl({
                    src: imagesStructure.desktop_image
                })
            }
            if (imagesStructure.mobile_image) {
                response.mobileImage = getFastlyUrl({
                    src: imagesStructure.mobile_image
                })
            }
        } catch (e) {
            console.warn(
                `[PageBuilder] Failed to parse data-background-images attribute on node:`,
                node,
                `Value: "${images}"`,
                e
            )
        }
    }

    return response
}

const alignmentToFlex = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end'
}

/**
 * Retrieve vertical alignment from a master format node
 *
 * @param node
 * @returns {{verticalAlignment: null}}
 */
export function getVerticalAlignment(node) {
    let verticalAlignment = null
    if (node.style.justifyContent) {
        verticalAlignment = flexToVerticalAlignment(node.style.justifyContent)
    }

    // Fallback for PageBuilder custom vertical-alignment style
    if (
        !verticalAlignment &&
        node.getAttribute('style')?.includes('vertical-alignment')
    ) {
        const match = node
            .getAttribute('style')
            .match(/vertical-alignment:\s*([^;]+)/)
        if (match) {
            verticalAlignment = match[1].trim()
        }
    }

    return {
        verticalAlignment
    }
}

/**
 * Convert vertical alignment values to flex values
 *
 * @param alignment
 * @returns {*}
 */
export function verticalAlignmentToFlex(alignment) {
    return alignmentToFlex[alignment]
}

/**
 * Convert flex to vertical alignment values
 *
 * @param flex
 * @returns {*}
 */
export function flexToVerticalAlignment(flex) {
    const flexToAlignment = Object.assign(
        {},
        ...Object.entries(alignmentToFlex).map(([a, b]) => ({ [b]: a }))
    )
    return flexToAlignment[flex]
}

/**
 * Retrieve advanced props from content type node
 *
 * @param node
 * @returns {{border: (string|string[]|string), marginRight: (*|string), borderColor: *, paddingBottom: (*|number|string), borderRadius: *, borderWidth: *, paddingRight: (*|number|string), marginBottom: (*|string), paddingTop: (*|string), paddingLeft: (*|string), marginTop: (*|string), marginLeft: (*|string|{get}|number)}}
 */
export function getAdvanced(node) {
    return {
        ...getPadding(node),
        ...getMargin(node),
        ...getBorder(node),
        ...getTextAlign(node),
        ...getCssClasses(node),
        ...getIsHidden(node)
    }
}

/**
 * Normalize inline style values read from DOM (jsdom vs DOMParser differ on empty props).
 * Returns null (not undefined) so getStaticProps can serialize the PageBuilder tree.
 */
export function readInlineStyleValue(value) {
    if (value == null || value === '') {
        return null
    }
    return value
}

/**
 * Drop empty/null style entries so SSR and client serialize the same inline style string.
 * Expand `borderColor` to longhands: React SSR writes border-*-color in HTML while the
 * client keeps the camelCase shorthand, which triggers hydration mismatches.
 */
export function compactInlineStyles(styles) {
    if (!styles || typeof styles !== 'object') {
        return styles
    }

    const next = {}

    for (const [key, value] of Object.entries(styles)) {
        if (value == null || value === false) {
            continue
        }
        if (typeof value === 'string' && value.trim() === '') {
            continue
        }

        if (key === 'borderColor') {
            next.borderTopColor = value
            next.borderRightColor = value
            next.borderBottomColor = value
            next.borderLeftColor = value
            continue
        }

        next[key] = value
    }

    return next
}

/**
 * Retrieve the padding from a content type node
 *
 * @param node
 * @returns {{paddingBottom: *, paddingRight: *, paddingTop: *, paddingLeft: *}}
 */
export function getPadding(node) {
    return {
        paddingTop: readInlineStyleValue(node.style.paddingTop),
        paddingRight: readInlineStyleValue(node.style.paddingRight),
        paddingBottom: readInlineStyleValue(node.style.paddingBottom),
        paddingLeft: readInlineStyleValue(node.style.paddingLeft)
    }
}

/**
 * Retrieve the margin from a content type node
 *
 * @param node
 * @returns {{marginRight: *, marginBottom: *, marginTop: *, marginLeft: *}}
 */
export function getMargin(node) {
    return {
        marginTop: readInlineStyleValue(node.style.marginTop),
        marginRight: readInlineStyleValue(node.style.marginRight),
        marginBottom: readInlineStyleValue(node.style.marginBottom),
        marginLeft: readInlineStyleValue(node.style.marginLeft)
    }
}

/**
 * Retrieve the border from a content type node
 *
 * @param node
 * @returns {{border: (string|string), borderColor: *, borderRadius: *, borderWidth: *}}
 */
export function getBorder(node) {
    return {
        border: readInlineStyleValue(node.style.borderStyle),
        borderColor: readInlineStyleValue(node.style.borderColor),
        borderWidth: readInlineStyleValue(node.style.borderWidth),
        borderRadius: readInlineStyleValue(node.style.borderRadius)
    }
}

/**
 * Retrieve the text align from a content type node
 *
 * @param node
 * @returns {{textAlign: *}}
 */
export function getTextAlign(node) {
    return {
        textAlign: readInlineStyleValue(node.style.textAlign)
    }
}

/**
 * Retrieve the CSS classes from a content type node
 * @param node
 * @returns {{cssClasses: any}}
 */
export function getCssClasses(node) {
    return {
        cssClasses: node.getAttribute('class')
            ? node.getAttribute('class').split(' ')
            : []
    }
}

/**
 * Retrieve the image quality from CSS classes
 * @param string[]
 * @returns {number}
 */
export function getImageQuality(classList, defaultQuality = 80) {
    const qualityClassList = classList.filter((classname) =>
        classname.startsWith('quality-')
    )
    if (qualityClassList.length === 0) return defaultQuality
    else return Number(qualityClassList[0].substring('quality-'.length))
}

/**
 * Retrieve if CSS display property is set to none from a content type node
 *
 * @param node
 * @returns {{isHidden: boolean}}
 */
export function getIsHidden(node) {
    return {
        isHidden: node.style.display === 'none'
    }
}

/**
 * Decode HTML entities (Magento PB often stores markup as &lt;style&gt;...).
 */
export function decodeHtmlEntities(raw = '') {
    if (!raw || !/&(?:#\d+|#x[\da-f]+|\w+);/i.test(raw)) {
        return raw
    }

    return raw
        .replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
        )
        .replace(/&#(\d+);/g, (_, num) =>
            String.fromCharCode(parseInt(num, 10))
        )
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#039;/g, "'")
        .replace(/&amp;/g, '&')
}

/**
 * Extract <style> blocks from an HTML string (fallback when tags are plain text).
 */
export function extractStylesFromHtmlString(raw = '') {
    if (!raw || typeof raw !== 'string') {
        return { html: '', cssBlocks: [] }
    }

    const cssBlocks = []
    let html = raw

    html = html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (_, css) => {
        const trimmed = css.trim()
        if (trimmed) {
            cssBlocks.push(trimmed)
        }
        return ''
    })

    return {
        html: html.trim(),
        cssBlocks
    }
}

/**
 * Decode entities then strip embedded <style> tags for React 18 innerHTML safety.
 */
export function normalizePageBuilderHtml(raw = '') {
    const decoded = decodeHtmlEntities(raw)
    return extractStylesFromHtmlString(decoded)
}

const PAGE_BUILDER_HTML_FIELDS = ['html', 'content', 'richContent']

/**
 * Next.js getStaticProps cannot serialize undefined — convert to null recursively.
 */
export function normalizePageBuilderPropsForSerialization(value) {
    if (value === undefined) {
        return null
    }

    if (Array.isArray(value)) {
        return value.map(normalizePageBuilderPropsForSerialization)
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([key, entryValue]) => [
                key,
                normalizePageBuilderPropsForSerialization(entryValue)
            ])
        )
    }

    return value
}

/**
 * Walk a parsed PageBuilder tree and ensure no node carries raw <style> in HTML fields.
 */
export function sanitizePageBuilderTree(tree) {
    if (!tree || typeof tree !== 'object') {
        return tree
    }

    PAGE_BUILDER_HTML_FIELDS.forEach((field) => {
        if (typeof tree[field] === 'string' && tree[field]) {
            const { html, cssBlocks } = normalizePageBuilderHtml(tree[field])
            tree[field] = html
            if (cssBlocks.length) {
                tree.cssBlocks = [...(tree.cssBlocks || []), ...cssBlocks]
            }
        }
    })

    if (Array.isArray(tree.children)) {
        tree.children.forEach(sanitizePageBuilderTree)
    }

    return normalizePageBuilderPropsForSerialization(tree)
}

/**
 * Page Builder HTML blocks often embed <style> tags. Injecting them via
 * dangerouslySetInnerHTML on a div renders CSS as visible text in React 18.
 */
export function extractStylesFromHtmlNode(node) {
    if (!node || typeof node.cloneNode !== 'function') {
        return { html: '', cssBlocks: [] }
    }

    const clone = node.cloneNode(true)
    const cssBlocks = []

    clone.querySelectorAll('style').forEach((styleEl) => {
        const css = styleEl.textContent?.trim()
        if (css) {
            cssBlocks.push(css)
        }
        styleEl.remove()
    })

    const html = (clone.innerHTML || '').trim()

    if (cssBlocks.length > 0) {
        return { html, cssBlocks }
    }

    return extractStylesFromHtmlString(
        decodeHtmlEntities(node.innerHTML || node.textContent || html)
    )
}

/**
 * Remove all <style> tags from the parsed document after inline conversion.
 */
export function extractAllDocumentStyles(rootEl) {
    if (!rootEl || typeof rootEl.querySelectorAll !== 'function') {
        return []
    }

    const cssBlocks = []

    rootEl.querySelectorAll('style').forEach((styleEl) => {
        const css = styleEl.textContent?.trim()
        if (css) {
            cssBlocks.push(css)
        }
        styleEl.remove()
    })

    return cssBlocks
}
